'use client';

import { type ComponentPropsWithoutRef, forwardRef, memo, useId } from 'react';

import { cn } from '@air/utils-tailwind';

export type LabeledInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'size'> & {
  label: string;
  hideSpinner?: boolean;
  size?: 'medium' | 'small';
};

export const LabeledInput = memo(
  forwardRef<HTMLInputElement, LabeledInputProps>(
    ({ className, hideSpinner, label, size = 'medium', ...restOfProps }, forwardedRef) => {
      const generatedId = useId();
      const inputId = restOfProps.id ?? generatedId;

      return (
        <div
          className={cn(
            'flex items-center gap-1 rounded-8 border border-neutral-6 bg-white px-4 focus-within:border-brand-9 focus-within:ring-2 focus-within:ring-brand-9/20 dark:bg-neutral-1',
            size === 'small' ? 'h-8 px-2' : 'h-10',
            className,
          )}
        >
          <label className="shrink-0 text-12 text-neutral-9" htmlFor={inputId}>
            {label}
          </label>
          <input
            className={cn(
              'min-w-0 flex-1 bg-transparent text-right text-12 text-neutral-12 outline-none',
              hideSpinner &&
                '[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            )}
            id={inputId}
            {...restOfProps}
            ref={forwardedRef}
          />
        </div>
      );
    },
  ),
);

LabeledInput.displayName = 'LabeledInput';
